import { useState } from "react";

import { Badge, Card, Field, Fields } from "../../../components/ui";
import { ResourceView } from "../../../components/states";
import type { AppConfig } from "../../../config";
import { useResource } from "../../../hooks/useResource";
import {
  domainTurnsTodayOf,
  labeebTenantUsagePath,
  type LabeebUsage as LabeebUsageShape,
} from "../../../api/labeeb";
import { LabeebMissing } from "./labeebMissing";

/**
 * "12 of 50" / "12" / "—", from the new flat counters.
 *
 * Replaces `describeCounter`, which read the old `{messages, limit}` object.
 * A count with no limit is real (an unlimited tier), and must not render as
 * "12 of undefined"; no count at all is a dash, never a zero — those say
 * different things to an operator chasing a quota complaint.
 */
function describeUsage(used: number | undefined, limit: number | undefined): string {
  if (typeof used !== "number") return "—";
  if (typeof limit !== "number" || limit < 0) return String(used);
  return `${used} of ${limit}`;
}

/**
 * Today and this month against the envelope.
 *
 * NOT a 30-day chart: neither product serves a usage-history route — MCP gives
 * the current UTC day and Labeeb today/this month — so this shows those two
 * numbers and does not invent a series.
 *
 * `per_agent_today` is rendered apart from the enforced totals because it is
 * recorded but NOT enforced; folding it in would imply a limit nothing checks.
 */
export function LabeebUsage({
  config,
  tenantId,
}: {
  config: AppConfig;
  tenantId: string;
}) {
  // The standalone route, which nothing called until now even though
  // labeebTenantUsagePath() has been exported all along. The tenant detail
  // also nests `usage`, but the dedicated route is the one that stays correct
  // if the detail response is ever trimmed.
  const [date, setDate] = useState("");
  const usagePath = /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? `${labeebTenantUsagePath(tenantId)}?date=${date}`
    : labeebTenantUsagePath(tenantId);
  const usage = useResource<LabeebUsageShape>(config, usagePath, [tenantId, date]);
  // The detail route no longer bundles a usage object, so this read is the
  // only source. There is nothing left to fall back to.
  const u = usage.state.kind === "ready" ? usage.state.data : null;

  return (
    <Card title="Usage">
      <ResourceView
        state={usage.state}
        what="usage"
        onRetry={usage.reload}
        notFound={<LabeebMissing state={usage.state} />}
      />

      {usage.state.kind === "ready" && (
        <>
          {/* Flat integers with their own limits now, instead of the old
              nested `{messages, limit}` objects — and the API serves the
              window boundary too, so the panel can say when the allowance
              returns rather than implying the limit is permanent. */}
          <Fields>
            <Field label="Today">{describeUsage(u?.messages_today, u?.daily_limit)}</Field>
            <Field label="This month">
              {describeUsage(u?.messages_this_month, u?.monthly_limit)}
            </Field>
            <Field label="Day resets">{u?.day_resets_at ?? "—"}</Field>
            <Field label="Month resets">{u?.month_resets_at ?? "—"}</Field>
          </Fields>
          <div className="mt-3">
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Usage date
              <input
                type="date"
                aria-label="Usage date"
                className="w-40 rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Per-domain turns are returned only for a chosen date. They are recorded, not
              enforced.
            </p>
            {date && domainTurnsTodayOf(u).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-2">
                {domainTurnsTodayOf(u).map((row) => (
                  <Badge key={row.domain}>
                    {row.domain}: {row.turns}
                  </Badge>
                ))}
              </div>
            )}
            {date && domainTurnsTodayOf(u).length === 0 && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                No per-domain turns on this date.
              </p>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
